import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Address from 'components/Address';
import Button from 'components/Button';
import Paper from 'components/Paper';
import Checkbox, { CheckboxesArea } from 'components/Checkbox';
import {
  MAKE_THIS_MY_PRIMARY_ADDRESS_TEXT,
  MAKE_THIS_MY_PRIMARY_ADDRESS_TITLE,
  MAKE_THIS_MY_DELIVERY_MULTIPLE_SUB_TEXT,
  MAKE_THIS_MY_DELIVERY_SINGLE_SUB_TEXT,
  MAKE_THIS_MY_DELIVERY_TITLE,
  SAVE_ADDRESS_CHANGE_DELAYED_TEXT,
} from 'helpers/copyHelper';

const StyledAddressSetConfirmView = styled.div`
  > * {
    margin-bottom: ${({ theme }) => theme.spacing * 2}px;
  }
  .changeDelayedText {
    margin: 0 0 0 ${({ theme }) => theme.spacing}px;
  }

  .Address {
    li:first-child{
        font-family: ${({ theme }) => theme.typography.h3.fontFamily};
        font-weight: ${({ theme }) => theme.typography.h3.fontWeight};
        font-size: ${({ theme }) => theme.typography.h3.fontSize};
      }
  }
`;

const AddressSetConfirmView = ({
  className,
  address,
  onSubmit,
  onCancel,
  changeDelayed,
  multipleSubs,
  forceMakePrimary,
}) => {
  const [makePrimary, setMakePrimary] = useState(forceMakePrimary);
  const [applyToAll, setApplyToAll] = useState(false);
  const [pendingConfirmed, setPendingConfirmed] = useState(!changeDelayed);
  const classes = ['AddressSetConfirmView', className];

  useEffect(() => {
    setPendingConfirmed(false);
  }, [applyToAll]);

  return (
    <StyledAddressSetConfirmView
      className={classes.filter((c) => !!c).join(' ')}
    >
      <h1>Change address</h1>
      <p>Set this address as your delivery address or primary address</p>
      <Paper>
        <Address address={address} />
      </Paper>
      <CheckboxesArea>
        <Checkbox
          onChange={(e) => setApplyToAll(e.target.checked)}
          checked={applyToAll}
          dataLayer={{ action: 'Make This My Delivery Address Click' }}
        >
          <h4>{MAKE_THIS_MY_DELIVERY_TITLE()}</h4>
          <p className="actionMessage">
            {multipleSubs
              ? MAKE_THIS_MY_DELIVERY_MULTIPLE_SUB_TEXT()
              : MAKE_THIS_MY_DELIVERY_SINGLE_SUB_TEXT()}
          </p>
        </Checkbox>
        <Checkbox
          onChange={(e) => setMakePrimary(e.target.checked)}
          readOnly={forceMakePrimary}
          checked={makePrimary}
          dataLayer={{ action: 'Make This My Primary Address Click' }}
        >
          <h4>{MAKE_THIS_MY_PRIMARY_ADDRESS_TITLE()}</h4>
          <p>
            {MAKE_THIS_MY_PRIMARY_ADDRESS_TEXT()}
          </p>
        </Checkbox>
        {changeDelayed && (
          <Checkbox
            onChange={(e) => setPendingConfirmed(e.target.checked)}
            disabled={!applyToAll}
            checked={pendingConfirmed}
          >
            <p className="changeDelayedText">{SAVE_ADDRESS_CHANGE_DELAYED_TEXT()}</p>
          </Checkbox>
        )}
      </CheckboxesArea>
      <Button
        onClick={() => onSubmit(address, makePrimary, applyToAll)}
        disabled={
          !(applyToAll || makePrimary) || (applyToAll && changeDelayed && !pendingConfirmed)
        }
        dataLayer={{ action: 'Make This My Primary Address', label: 'Set' }}
        fullWidth
      >
        Set
      </Button>
      <Button
        onClick={onCancel}
        inverse
        fullWidth
        dataLayer={{ action: 'Make This My Primary Address', label: 'Cancel' }}
      >
        Cancel
      </Button>
    </StyledAddressSetConfirmView>
  );
};

AddressSetConfirmView.displayName = 'AddressSetConfirmView';

AddressSetConfirmView.propTypes = {
  className: PropTypes.string,
  address: PropTypes.shape({}).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  changeDelayed: PropTypes.bool,
  multipleSubs: PropTypes.bool,
  forceMakePrimary: PropTypes.bool,
};

AddressSetConfirmView.defaultProps = {
  className: null,
  changeDelayed: false,
  multipleSubs: false,
  forceMakePrimary: false,
};

export default AddressSetConfirmView;
